<template>
    <div class="min-h-screen">
        <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
            <transition name="view-fade" mode="out-in">
                <div v-if="currentView === 'list'" key="list">
                    <header class="mb-8">
                        <h1
                            class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 text-center sm:text-left">
                            Investimentos <span class="text-brand-600 dark:text-brand-500">Compartilhados</span> </h1>
                        <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1"> Crie e
                            gerencie investimentos em grupo com outros usuários. </p>
                    </header>

                    <div class="mb-8 flex justify-start">
                        <button @click="handleNovoInvestimento"
                            class="btn-primary flex items-center justify-center text-sm shadow-lg hover:shadow-brand-700/50 transition-all">
                            <span class="material-symbols-outlined mr-2 text-lg">group_add</span> Criar Novo
                            Investimento </button>
                    </div>

                    <div v-if="convitesPendentes.length > 0" class="mb-12">
                        <h2
                            class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4 border-b pb-2 border-slate-200 dark:border-slate-700">
                            Convites Pendentes</h2>
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div v-for="convite in convitesPendentes" :key="convite.conviteId"
                                class="bg-white dark:bg-brand-800 p-5 rounded-2xl shadow-lg border-2 border-brand-500 animate-pulse-fast">
                                <p class="text-sm text-slate-600 dark:text-slate-400"> <strong
                                        class="text-slate-800 dark:text-slate-200">{{ convite.nomeConvidadoPor
                                        }}</strong> convidou você para: </p>
                                <h4 class="text-lg font-bold text-brand-600 dark:text-brand-500 mt-1 truncate">{{
                                    convite.nomeEntidade }}</h4>
                                <div class="flex items-center justify-end gap-3 mt-4"> <button
                                        @click="handleDecline(convite.conviteId)"
                                        class="btn-danger-outline !text-xs !py-1.5 !px-3">Recusar</button> <button
                                        @click="handleAccept(convite.conviteId)"
                                        class="btn-primary !text-xs !py-1.5 !px-3">Aceitar</button> </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="i in 3" :key="i" class="skeleton-card"></div>
                    </div>
                    <div v-else-if="portfolios.length === 0 && convitesPendentes.length === 0" class="empty-state">
                        <span
                            class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">folder_shared</span>
                        <p class="mt-4 font-medium">Você ainda não faz parte de nenhum investimento.</p>
                        <p class="text-sm">Crie um novo ou aceite um convite para começar.</p>
                    </div>
                    <transition-group v-else name="list" tag="div"
                        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="portfolio in portfolios" :key="portfolio.uuid"
                            @click="selectPortfolio(portfolio.uuid)" class="card-portfolio group">
                            <div>
                                <h3
                                    class="font-bold text-xl text-slate-800 dark:text-slate-100 truncate group-hover:text-brand-600 dark:group-hover:text-brand-500 transition-colors">
                                    {{ portfolio.nome }}</h3>
                                <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 h-10 overflow-hidden">{{
                                    portfolio.descricao }}</p>
                                <div
                                    class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                    <span class="text-sm font-medium text-slate-500 dark:text-slate-400">Membros</span>
                                    <div class="flex items-center -space-x-2">
                                        <div v-for="membro in portfolio.membros.slice(0, 4)" :key="membro.id"
                                            class="w-8 h-8 rounded-full bg-slate-100 dark:bg-brand-700 text-brand-600 dark:text-brand-500 flex items-center justify-center text-xs font-bold ring-2 ring-white dark:ring-brand-800"
                                            :title="membro.nome">{{ membro.nome.charAt(0).toUpperCase() }}</div>
                                        <div v-if="portfolio.membros.length > 4"
                                            class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-bold ring-2 ring-white dark:ring-brand-800">
                                            +{{ portfolio.membros.length - 4 }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 text-right">
                                <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">Valor Total</p>
                                <p class="font-bold text-3xl text-slate-800 dark:text-slate-100 tracking-tight">{{
                                    displayCurrency(portfolio.valorTotal, true) }}</p>
                            </div>
                        </div>
                    </transition-group>
                </div>

                <div v-else-if="currentView === 'details' && selectedPortfolio" key="details">
                    <button @click="returnToList"
                        class="mb-4 flex items-center text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-500/80 transition-colors">
                        <span class="material-symbols-outlined mr-1">arrow_back</span>Voltar para a Lista </button>

                    <header class="bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg mb-8">
                        <div class="md:flex justify-between items-start">
                            <div class="flex items-center">
                                <div>
                                    <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">{{
                                        selectedPortfolio.nome }}</h1>
                                    <p class="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">{{
                                        selectedPortfolio.descricao }}</p>
                                </div>
                                <button @click="toggleVisibility"
                                    class="ml-3 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-2 rounded-full transition-colors flex-shrink-0"
                                    :title="showValues ? 'Ocultar valores' : 'Mostrar valores'"> <span v-if="showValues"
                                        class="material-symbols-outlined">visibility</span> <span v-else
                                        class="material-symbols-outlined">visibility_off</span> </button>
                            </div>
                            <div class="flex flex-wrap gap-3 mt-4 md:mt-0 flex-shrink-0">
                                <button @click="handleNovoAtivo" class="btn-primary flex-1 sm:flex-none"> <span
                                        class="material-symbols-outlined mr-2 text-lg">add_circle</span> Adicionar Ativo
                                </button>
                                <button v-if="isOwner" @click="showInviteModal = true"
                                    class="btn-secondary flex-1 sm:flex-none"> <span
                                        class="material-symbols-outlined mr-2 text-lg">person_add</span> Convidar
                                </button>
                                <button @click="handleEditInvestimento" class="btn-secondary flex-1 sm:flex-none"> <span
                                        class="material-symbols-outlined mr-2 text-lg">edit</span> Editar </button>
                            </div>
                        </div>
                        <div
                            class="mt-6 bg-gradient-to-r from-brand-600 to-brand-700 text-white p-6 rounded-xl shadow-lg flex flex-col sm:flex-row justify-between items-center">
                            <div class="mb-2 sm:mb-0 text-center sm:text-left"><span class="text-lg font-medium">Valor
                                    Total do Investimento</span></div>
                            <div><span class="text-4xl font-bold tracking-tight">{{
                                displayCurrency(selectedPortfolio.valorTotal) }}</span></div>
                        </div>
                    </header>

                    <div class="grid grid-cols-1 lg:grid-cols-5 gap-8">
                        <div class="lg:col-span-3">
                            <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Ativos no
                                Investimento</h2>
                            <div v-if="!selectedPortfolio.ativos || selectedPortfolio.ativos.length === 0"
                                class="empty-state"> <span
                                    class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">savings</span>
                                <p class="mt-4 font-medium">Nenhum ativo neste investimento.</p>
                            </div>
                            <div v-else class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg">
                                <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                                    <li v-for="item in selectedPortfolio.ativos" :key="item.id"
                                        class="p-4 flex items-center justify-between group">
                                        <div class="flex items-center overflow-hidden">
                                            <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                                :class="getInvestmentTheme(item.tipo).bg"> <span
                                                    class="material-symbols-outlined text-xl"
                                                    :class="getInvestmentTheme(item.tipo).text_contrast">{{
                                                    getIconForTipo(item.tipo) }}</span> </div>
                                            <div class="ml-4 truncate">
                                                <p class="font-semibold text-slate-800 dark:text-slate-100 truncate">{{
                                                    item.descricao }}</p>
                                                <p class="text-sm text-slate-500 dark:text-slate-400 truncate"> {{
                                                    displayCurrency(item.valorAtual) }} <span v-if="item.ticker"
                                                        class="font-mono text-xs">- {{ item.ticker }}</span> <span
                                                        class="mx-1 text-slate-300 dark:text-slate-600">•</span>
                                                    <span>Por: {{ item.criadoPorNome || 'N/A' }}</span> </p>
                                            </div>
                                        </div>
                                        <div
                                            class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                            <button @click.stop="handleEditAtivo(item)" title="Editar"
                                                class="btn-action-icon text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-slate-700"><span
                                                    class="material-symbols-outlined text-lg">edit</span></button>
                                            <button @click.stop="confirmDeleteAtivo(item)" title="Excluir"
                                                class="btn-action-icon text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-slate-700"><span
                                                    class="material-symbols-outlined text-lg">delete</span></button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="lg:col-span-2 space-y-8">
                            <div>
                                <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Membros</h2>
                                <div v-if="!selectedPortfolio.membros || selectedPortfolio.membros.length === 0"
                                    class="empty-state h-full flex-col justify-center"> <span
                                        class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">group_off</span>
                                    <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhum membro
                                        encontrado.</p>
                                </div>
                                <div v-else class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg">
                                    <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                                        <li v-for="membro in selectedPortfolio.membros" :key="membro.id"
                                            class="p-4 flex items-center justify-between transition-opacity"
                                            :class="{ 'opacity-60': membro.status === 'PENDENTE' }">
                                            <div class="flex items-center">
                                                <div
                                                    class="w-10 h-10 rounded-full bg-slate-100 dark:bg-brand-700 text-brand-600 dark:text-brand-500 flex items-center justify-center text-sm font-bold ring-2 ring-white dark:ring-brand-800 flex-shrink-0">
                                                    {{ membro.nome.charAt(0).toUpperCase() }}</div>
                                                <div class="ml-4">
                                                    <p
                                                        class="font-semibold text-slate-800 dark:text-slate-100 flex items-center">
                                                        {{ membro.nome }} <span
                                                            v-if="membro.id === selectedPortfolio.criadorId"
                                                            class="tag-owner">Dono</span> <span
                                                            v-if="membro.status === 'PENDENTE'"
                                                            class="tag-pending">Pendente</span> </p>
                                                    <p class="text-sm text-slate-500 dark:text-slate-400">{{
                                                        membro.email }}</p>
                                                </div>
                                            </div>
                                            <div class="flex items-center"> <button
                                                    v-if="isOwner && membro.id !== currentUser.id && membro.status === 'ATIVO'"
                                                    @click="confirmRemoveMember(membro)"
                                                    class="btn-danger-outline !p-2 !rounded-full"
                                                    title="Remover membro"><span
                                                        class="material-symbols-outlined text-lg">person_remove</span></button>
                                                <button
                                                    v-if="isOwner && membro.id !== currentUser.id && membro.status === 'PENDENTE'"
                                                    @click="confirmCancelInvite(membro)"
                                                    class="btn-secondary !p-2 !rounded-full"
                                                    title="Cancelar convite"><span
                                                        class="material-symbols-outlined text-lg">cancel</span></button>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Zona de Perigo
                                </h2>
                                <div class="danger-zone">
                                    <div v-if="isOwner" class="w-full">
                                        <h4 class="font-bold text-red-800 dark:text-red-300">Excluir Investimento</h4>
                                        <p class="text-sm text-red-600 dark:text-red-300/80 mb-2">Esta ação é
                                            irreversível e excluirá todos os ativos.</p> <button
                                            @click="confirmDeletePortfolio"
                                            class="btn-danger-outline flex items-center text-sm"> <span
                                                class="material-symbols-outlined mr-2 text-lg">delete_forever</span>
                                            Excluir </button>
                                    </div>
                                    <div v-if="!isOwner" class="w-full">
                                        <h4 class="font-bold text-red-800 dark:text-red-300">Sair do Investimento</h4>
                                        <p class="text-sm text-red-600 dark:text-red-300/80 mb-2">Você perderá o acesso
                                            a este investimento.</p> <button @click="confirmLeavePortfolio"
                                            class="btn-danger-outline flex items-center text-sm"> <span
                                                class="material-symbols-outlined mr-2 text-lg">logout</span> Sair do
                                            Investimento </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <transition name="modal-fade">
                <div v-if="showCreateModal" @click.self="showCreateModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>Criar Novo Investimento</h3><button @click="showCreateModal = false"
                                class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleCreatePortfolio" class="p-6 space-y-4">
                            <div><label for="pNome" class="form-label">Nome do Investimento</label><input id="pNome"
                                    v-model="newPortfolio.nome" type="text" required
                                    placeholder="Ex: Investimento em Bitcoin" class="form-input" /></div>
                            <div><label for="pDesc" class="form-label">Descrição</label><input id="pDesc"
                                    v-model="newPortfolio.descricao" type="text" placeholder="Objetivos do grupo"
                                    class="form-input" /></div>
                            <div><label for="pEmails" class="form-label">Convidar Membros (opcional)</label><textarea
                                    id="pEmails" v-model="newPortfolio.emailsMembros" rows="3"
                                    placeholder="Separe os emails por vírgula ou espaço" class="form-input"></textarea>
                            </div>
                            <footer class="modal-footer"><button @click="showCreateModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmittingCreate" class="btn-primary flex items-center"><span
                                        v-if="isSubmittingCreate"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmittingCreate ? 'Criando...' : 'Criar Investimento' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showInviteModal" @click.self="showInviteModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>Convidar Membro</h3><button @click="showInviteModal = false"
                                class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleInviteMember" class="p-6 space-y-4">
                            <div><label for="inviteEmail" class="form-label">Email do Convidado</label><input
                                    id="inviteEmail" v-model="inviteForm.email" type="email" required
                                    placeholder="nome@exemplo.com" class="form-input" /></div>
                            <footer class="modal-footer"><button @click="showInviteModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmittingInvite" class="btn-primary flex items-center"><span
                                        v-if="isSubmittingInvite"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmittingInvite ? 'Convidando...' : 'Enviar Convite' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showAtivoModal" @click.self="showAtivoModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>{{ isEditingAtivo ? 'Editar Ativo' : 'Adicionar Ativo' }}</h3><button
                                @click="showAtivoModal = false" class="modal-close-btn"><span
                                    class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleSubmitAtivo" class="p-6 space-y-4">
                            <div><label for="invDescricao" class="form-label">Descrição</label><input id="invDescricao"
                                    v-model="ativoForm.descricao" type="text" required placeholder="Ex: Ações Petrobras"
                                    class="form-input" /></div>
                            <div><label for="invTipo" class="form-label">Tipo de Investimento</label><select
                                    id="invTipo" v-model="ativoForm.tipo" required class="form-input">
                                    <option disabled value="">Selecione um tipo</option>
                                    <option v-for="t in tiposInvestimento" :key="t.value" :value="t.value">{{ t.label }}
                                    </option>
                                </select></div>
                            <div v-if="showTickerInput"><label for="invTicker" class="form-label">Ticker</label><input
                                    id="invTicker" v-model="ativoForm.ticker" type="text" placeholder="Ex: PETR4"
                                    class="form-input" /></div>
                            <div><label for="invInstituicao" class="form-label">Instituição / Corretora</label><input
                                    id="invInstituicao" v-model="ativoForm.instituicao" type="text"
                                    placeholder="Ex: XP, Nubank" class="form-input" /></div>
                            <div><label for="invValor" class="form-label">Valor Atual (R$)</label><input id="invValor"
                                    v-model.number="ativoForm.valorAtual" type="number" step="0.01" required
                                    placeholder="0.00" class="form-input" /></div>
                            <footer class="modal-footer"><button @click="showAtivoModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmittingAtivo" class="btn-primary flex items-center"><span
                                        v-if="isSubmittingAtivo"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmittingAtivo ? 'Salvando...' : 'Salvar Ativo' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false" class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">warning</span>
                            <h3 class="modal-title">Excluir Ativo</h3>
                            <p class="modal-text">Tem certeza que deseja excluir o ativo <strong>"{{
                                    ativoParaDeletar?.descricao }}"</strong>? Esta ação não pode ser desfeita.</p>
                        </div>
                        <div class="modal-footer"><button @click="showDeleteConfirm = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeDeleteAtivo"
                                class="btn-danger">Sim, Excluir</button></div>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showEditPortfolioModal" @click.self="showEditPortfolioModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>Editar Investimento</h3><button @click="showEditPortfolioModal = false"
                                class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleUpdatePortfolio" class="p-6 space-y-4">
                            <div><label for="pEditNome" class="form-label">Nome do Investimento</label><input
                                    id="pEditNome" v-model="editPortfolioForm.nome" type="text" required
                                    class="form-input" /></div>
                            <div><label for="pEditDesc" class="form-label">Descrição</label><input id="pEditDesc"
                                    v-model="editPortfolioForm.descricao" type="text" class="form-input" /></div>
                            <footer class="modal-footer"><button @click="showEditPortfolioModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmittingPortfolio" class="btn-primary flex items-center"><span
                                        v-if="isSubmittingPortfolio"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmittingPortfolio ? 'Salvando...' : 'Salvar Alterações' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmDeletePortfolio" @click.self="showConfirmDeletePortfolio = false"
                    class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">delete_forever</span>
                            <h3 class="modal-title">Excluir Investimento?</h3>
                            <p class="modal-text">Esta ação é irreversível e excluirá <strong>todos os ativos</strong>
                                dentro de "{{ selectedPortfolio?.nome }}".</p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmDeletePortfolio = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeDeletePortfolio"
                                class="btn-danger">Sim, Excluir</button></div>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmRemoveMember" @click.self="showConfirmRemoveMember = false"
                    class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">person_remove</span>
                            <h3 class="modal-title">Remover Membro?</h3>
                            <p class="modal-text">Tem certeza que deseja remover <strong>{{ memberToRemove?.nome
                                    }}</strong> do investimento <strong>"{{ selectedPortfolio?.nome }}"</strong>?</p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmRemoveMember = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeRemoveMember"
                                class="btn-danger">Sim, Remover</button></div>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmCancelInvite" @click.self="showConfirmCancelInvite = false"
                    class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">cancel</span>
                            <h3 class="modal-title">Cancelar Convite?</h3>
                            <p class="modal-text">Tem certeza que deseja cancelar o convite enviado para <strong>{{
                                    inviteToCancel?.nome }}</strong>?</p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmCancelInvite = false" type="button"
                                class="btn-secondary">Voltar</button><button @click="executeCancelInvite"
                                class="btn-danger">Sim, Cancelar</button></div>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmLeave" @click.self="showConfirmLeave = false" class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">logout</span>
                            <h3 class="modal-title">Sair do Investimento?</h3>
                            <p class="modal-text">Tem certeza que deseja sair do investimento <strong>"{{
                                    selectedPortfolio?.nome }}"</strong>?</p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmLeave = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeLeavePortfolio"
                                class="btn-danger">Sim, Sair</button></div>
                    </div>
                </div>
            </transition>

            <transition name="notification">
                <div v-if="notification.show" class="hidden md:block fixed top-5 right-5 w-full max-w-sm z-[70]">
                    <div class="p-4 rounded-lg shadow-2xl flex items-start text-white"
                        :class="notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'">
                        <div class="flex-shrink-0"><span class="material-symbols-outlined">{{ notification.type ===
                            'success' ? 'check_circle' : 'error' }}</span></div>
                        <div class="ml-3 flex-1">
                            <p class="text-sm font-bold">{{ notification.message }}</p>
                        </div>
                        <div class="ml-auto pl-3"><button @click="notification.show = false"
                                class="-mx-1.5 -my-1.5 p-1.5 rounded-md hover:bg-black/20 focus:outline-none focus:ring-2 focus:ring-white"><span
                                    class="material-symbols-outlined text-lg">close</span></button></div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const router = useRouter()
const portfolios = ref([])
const convitesPendentes = ref([])
const selectedPortfolio = ref(null)
const currentUser = ref({ id: null })
const currentView = ref('list')
const isLoading = ref(true)
const showValues = ref(true)

const isMobile = ref(false)
const checkDevice = () => { isMobile.value = window.innerWidth < 640 }

const showCreateModal = ref(false)
const showInviteModal = ref(false)
const showAtivoModal = ref(false)
const showEditPortfolioModal = ref(false)
const showDeleteConfirm = ref(false)
const showConfirmDeletePortfolio = ref(false)
const showConfirmRemoveMember = ref(false)
const showConfirmLeave = ref(false)
const showConfirmCancelInvite = ref(false)

const isSubmittingCreate = ref(false)
const isSubmittingInvite = ref(false)
const isSubmittingAtivo = ref(false)
const isSubmittingPortfolio = ref(false)

const isEditingAtivo = ref(false)
const ativoParaDeletar = ref(null)
const memberToRemove = ref(null)
const inviteToCancel = ref(null)
const newPortfolio = ref({ nome: '', descricao: '', emailsMembros: '' })
const inviteForm = ref({ email: '' })
const ativoForm = ref(null)
const editPortfolioForm = ref({ nome: '', descricao: '' })

const notification = ref({ show: false, type: 'success', message: '' })
const notificationTimer = ref(null)

const API_BASE_URL = 'https://backend.controlease.com.br/api'
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })

const isOwner = computed(() => (currentUser.value?.id && selectedPortfolio.value?.criadorId) ? currentUser.value.id === selectedPortfolio.value.criadorId : false)

const tiposInvestimento = ref([
  { value: 'RENDA_FIXA', label: 'Renda Fixa' },
  { value: 'RENDA_VARIAVEL', label: 'Renda Variável (Ações, BDRs)' },
  { value: 'FUNDO_IMOBILIARIO', label: 'Fundo Imobiliário (FII)' },
  { value: 'TESOURO_DIRETO', label: 'Tesouro Direto' },
  { value: 'CRIPTOATIVO', label: 'Criptoativo' }
])

const investmentThemes = {
  RENDA_FIXA: { bg: 'bg-blue-600', text_contrast: 'text-white' },
  RENDA_VARIAVEL: { bg: 'bg-green-600', text_contrast: 'text-white' },
  FUNDO_IMOBILIARIO: { bg: 'bg-amber-600', text_contrast: 'text-white' },
  TESOURO_DIRETO: { bg: 'bg-indigo-600', text_contrast: 'text-white' },
  CRIPTOATIVO: { bg: 'bg-slate-700', text_contrast: 'text-white' }
}

const showTickerInput = computed(() => ativoForm.value && ['RENDA_VARIAVEL', 'FUNDO_IMOBILIARIO'].includes(ativoForm.value.tipo))
watch(ativoForm, (newVal) => { if (newVal && !showTickerInput.value) newVal.ticker = '' }, { deep: true })

const handleNovoInvestimento = () => {
  if (isMobile.value) {
    router.push({ name: 'InvestimentosCompartilhadosAdd' })
  } else {
    openCreatePortfolioModal()
  }
}

const handleEditInvestimento = () => {
  if (!selectedPortfolio.value) return
  if (isMobile.value) {
    router.push({ name: 'InvestimentosCompartilhadosEdit', params: { uuid: selectedPortfolio.value.uuid } })
  } else {
    openEditPortfolioModal()
  }
}

const handleNovoAtivo = () => {
  if (!selectedPortfolio.value) return
  if (isMobile.value) {
    router.push({ name: 'InvestimentoAtivoAdd', params: { portfolioUuid: selectedPortfolio.value.uuid } })
  } else {
    showAddAtivoModal()
  }
}

const handleEditAtivo = (itemToEdit) => {
  if (isMobile.value) {
    router.push({
      name: 'InvestimentoAtivoEdit',
      params: { ativoId: itemToEdit.id },
      state: { item: { ...itemToEdit, portfolio: { uuid: selectedPortfolio.value.uuid } } }
    })
  } else {
    editAtivo(itemToEdit)
  }
}

const showNotification = (type, message, duration = 4000) => {
  if (notificationTimer.value) clearTimeout(notificationTimer.value)
  notification.value = { show: true, type, message }
  notificationTimer.value = setTimeout(() => { notification.value.show = false }, duration)
}

const toggleVisibility = () => {
  showValues.value = !showValues.value
  localStorage.setItem('showFinancialValues', JSON.stringify(showValues.value))
}

const formatCurrency = (value) => {
  if (typeof value !== 'number') return '0,00'
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const displayCurrency = (value, forceShow = false) => {
  if (showValues.value || forceShow) return `R$ ${formatCurrency(value)}`
  return 'R$ ●●●●●'
}

const getIconForTipo = (tipo) => ({
  RENDA_FIXA: 'database',
  RENDA_VARIAVEL: 'trending_up',
  FUNDO_IMOBILIARIO: 'real_estate_agent',
  TESOURO_DIRETO: 'account_balance',
  CRIPTOATIVO: 'currency_bitcoin'
}[tipo] || 'monetization_on')

const getInvestmentTheme = (tipo) => investmentThemes[tipo] || investmentThemes.CRIPTOATIVO

const getInitialAtivoFormData = () => ({ id: null, descricao: '', tipo: 'RENDA_VARIAVEL', instituicao: '', ticker: '', valorAtual: null })

const selectPortfolio = async (uuid) => {
  isLoading.value = true
  try {
    const response = await axios.get(`${API_BASE_URL}/investimentos-compartilhados/${uuid}`, authConfig())
    selectedPortfolio.value = response.data
    currentView.value = 'details'
    if (route.params.id !== uuid) {
      router.push({ name: 'InvestimentosCompartilhados', params: { id: uuid } })
    }
  } catch {
    showNotification('error', 'Não foi possível carregar o investimento.')
    router.push({ name: 'InvestimentosCompartilhados' })
  } finally {
    isLoading.value = false
  }
}

const returnToList = async (updateRoute = true) => {
  currentView.value = 'list'
  selectedPortfolio.value = null
  if (updateRoute && route.params.id) {
    await router.push({ name: 'InvestimentosCompartilhados' })
  }
  await fetchPortfolios()
}

const fetchPortfolios = async () => {
  isLoading.value = true
  try {
    const response = await axios.get(`${API_BASE_URL}/investimentos-compartilhados`, authConfig())
    portfolios.value = response.data.investimentosAtivos || []
    convitesPendentes.value = response.data.convitesPendentes || []
  } catch {
    showNotification('error', 'Não foi possível carregar seus Investimentos.')
    portfolios.value = []
    convitesPendentes.value = []
  } finally {
    isLoading.value = false
  }
}

const fetchCurrentUser = () => {
  const userId = localStorage.getItem('usuario_id')
  if (userId) currentUser.value = { id: parseInt(userId, 10) }
}

const refreshSelectedPortfolio = async () => {
  if (!selectedPortfolio.value?.uuid) return
  try {
    const response = await axios.get(`${API_BASE_URL}/investimentos-compartilhados/${selectedPortfolio.value.uuid}`, authConfig())
    selectedPortfolio.value = response.data
  } catch {
    showNotification('error', 'Erro ao atualizar dados do investimento.')
    returnToList()
  }
}

const handleAccept = async (conviteId) => {
  try {
    await axios.post(`${API_BASE_URL}/convites/${conviteId}/aceitar`, {}, authConfig())
    showNotification('success', 'Convite aceito com sucesso!')
    fetchPortfolios()
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao aceitar convite.')
  }
}

const handleDecline = async (conviteId) => {
  try {
    await axios.post(`${API_BASE_URL}/convites/${conviteId}/recusar`, {}, authConfig())
    showNotification('success', 'Convite recusado.')
    convitesPendentes.value = convitesPendentes.value.filter(c => c.conviteId !== conviteId)
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao recusar convite.')
  }
}

const openCreatePortfolioModal = () => {
  newPortfolio.value = { nome: '', descricao: '', emailsMembros: '' }
  showCreateModal.value = true
}

const handleCreatePortfolio = async () => {
  isSubmittingCreate.value = true
  const emails = newPortfolio.value.emailsMembros.split(/[\s,]+/).filter(Boolean)
  const payload = { ...newPortfolio.value, emailsMembros: emails }
  try {
    const response = await axios.post(`${API_BASE_URL}/investimentos-compartilhados`, payload, authConfig())
    showCreateModal.value = false
    newPortfolio.value = { nome: '', descricao: '', emailsMembros: '' }
    await fetchPortfolios()
    if (response.data.uuid) {
      await selectPortfolio(response.data.uuid)
    }
    showNotification('success', 'Investimento criado e convites enviados!')
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Ocorreu um erro ao criar o investimento.')
  } finally {
    isSubmittingCreate.value = false
  }
}

const openEditPortfolioModal = () => {
  if (!selectedPortfolio.value) return
  editPortfolioForm.value.nome = selectedPortfolio.value.nome
  editPortfolioForm.value.descricao = selectedPortfolio.value.descricao
  showEditPortfolioModal.value = true
}

const handleUpdatePortfolio = async () => {
  isSubmittingPortfolio.value = true
  try {
    await axios.put(`${API_BASE_URL}/investimentos-compartilhados/${selectedPortfolio.value.uuid}`, editPortfolioForm.value, authConfig())
    showEditPortfolioModal.value = false
    await refreshSelectedPortfolio()
    showNotification('success', 'Investimento atualizado com sucesso!')
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao atualizar investimento.')
  } finally {
    isSubmittingPortfolio.value = false
  }
}

const confirmDeletePortfolio = () => { showConfirmDeletePortfolio.value = true }
const executeDeletePortfolio = async () => {
  showConfirmDeletePortfolio.value = false
  try {
    await axios.delete(`${API_BASE_URL}/investimentos-compartilhados/${selectedPortfolio.value.uuid}`, authConfig())
    await returnToList()
    showNotification('success', 'Investimento excluído com sucesso.')
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao excluir investimento.')
  }
}

const handleInviteMember = async () => {
  isSubmittingInvite.value = true
  try {
    await axios.post(`${API_BASE_URL}/investimentos-compartilhados/${selectedPortfolio.value.uuid}/convidar`, inviteForm.value, authConfig())
    showInviteModal.value = false
    inviteForm.value.email = ''
    await refreshSelectedPortfolio()
    showNotification('success', 'Membro convidado com sucesso!')
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Não foi possível convidar o membro.')
  } finally {
    isSubmittingInvite.value = false
  }
}

const confirmRemoveMember = (membro) => { memberToRemove.value = membro; showConfirmRemoveMember.value = true }
const executeRemoveMember = async () => {
  if (!memberToRemove.value) return
  showConfirmRemoveMember.value = false
  try {
    await axios.delete(`${API_BASE_URL}/investimentos-compartilhados/${selectedPortfolio.value.uuid}/membros/${memberToRemove.value.id}`, authConfig())
    await refreshSelectedPortfolio()
    showNotification('success', 'Membro removido com sucesso.')
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao remover membro.')
  } finally {
    memberToRemove.value = null
  }
}

const confirmCancelInvite = (membro) => { inviteToCancel.value = membro; showConfirmCancelInvite.value = true }
const executeCancelInvite = async () => {
  if (!inviteToCancel.value?.conviteId) return
  showConfirmCancelInvite.value = false
  try {
    await axios.delete(`${API_BASE_URL}/convites/${inviteToCancel.value.conviteId}/cancelar`, authConfig())
    await refreshSelectedPortfolio()
    showNotification('success', 'Convite cancelado com sucesso.')
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao cancelar o convite.')
  } finally {
    inviteToCancel.value = null
  }
}

const confirmLeavePortfolio = () => { showConfirmLeave.value = true }
const executeLeavePortfolio = async () => {
  showConfirmLeave.value = false
  try {
    await axios.delete(`${API_BASE_URL}/investimentos-compartilhados/${selectedPortfolio.value.uuid}/membros/leave`, authConfig())
    await returnToList()
    showNotification('success', 'Você saiu do investimento.')
  } catch (error) {
    showNotification('error', error.response?.data?.message || 'Erro ao sair do investimento.')
  }
}

const showAddAtivoModal = () => { isEditingAtivo.value = false; ativoForm.value = getInitialAtivoFormData(); showAtivoModal.value = true }
const editAtivo = (item) => { isEditingAtivo.value = true; ativoForm.value = { ...item }; showAtivoModal.value = true }

const handleSubmitAtivo = async () => {
  isSubmittingAtivo.value = true
  try {
    const payload = { ...ativoForm.value, ticker: ativoForm.value.ticker?.toUpperCase() }
    if (isEditingAtivo.value) {
      await axios.put(`${API_BASE_URL}/ativos-compartilhados/${payload.id}`, payload, authConfig())
    } else {
      await axios.post(`${API_BASE_URL}/investimentos-compartilhados/${selectedPortfolio.value.uuid}/ativos`, payload, authConfig())
    }
    showAtivoModal.value = false
    await refreshSelectedPortfolio()
    showNotification('success', `Ativo ${isEditingAtivo.value ? 'atualizado' : 'adicionado'} com sucesso!`)
  } catch {
    showNotification('error', 'Erro ao salvar ativo.')
  } finally {
    isSubmittingAtivo.value = false
  }
}

const confirmDeleteAtivo = (ativo) => { ativoParaDeletar.value = ativo; showDeleteConfirm.value = true }
const executeDeleteAtivo = async () => {
  if (!ativoParaDeletar.value) return
  showDeleteConfirm.value = false
  try {
    await axios.delete(`${API_BASE_URL}/ativos-compartilhados/${ativoParaDeletar.value.id}`, authConfig())
    await refreshSelectedPortfolio()
    showNotification('success', 'Ativo excluído com sucesso.')
  } catch {
    showNotification('error', 'Erro ao excluir ativo.')
  } finally {
    ativoParaDeletar.value = null
  }
}

onMounted(() => {
  const savedVisibility = localStorage.getItem('showFinancialValues')
  if (savedVisibility !== null) showValues.value = JSON.parse(savedVisibility)
  fetchCurrentUser()
  ativoForm.value = getInitialAtivoFormData()

  const id = route.params.id
  if (id) {
    selectPortfolio(id)
  } else {
    fetchPortfolios()
    currentView.value = 'list'
  }
  checkDevice()
  window.addEventListener('resize', checkDevice)
})

watch(() => route.params.id, (newId, oldId) => {
  if (newId && newId !== selectedPortfolio.value?.uuid) {
    selectPortfolio(newId)
  } else if (oldId && !newId) {
    if (currentView.value === 'details') {
      returnToList(false)
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', checkDevice)
  if (notificationTimer.value) clearTimeout(notificationTimer.value)
})
</script>


<style scoped>
.form-label {
    @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}

.form-input {
    @apply w-full px-3 py-2 text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700/60 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500/70 focus:border-brand-500;
}

.btn-primary {
    @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed;
}

.btn-secondary {
    @apply px-5 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-danger {
    @apply px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 ease-in-out;
}

.btn-danger-outline {
    @apply px-5 py-2.5 text-sm font-medium text-red-600 dark:text-red-500 bg-white dark:bg-brand-800 border border-red-600 dark:border-red-500 rounded-lg transition-colors duration-150 ease-in-out hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-action-icon {
    @apply p-1.5 rounded-full transition-colors duration-200;
}

.modal-backdrop {
    @apply fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 dark:bg-brand-900/80 backdrop-blur-sm p-4;
}

.modal-container {
    @apply bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg transform transition-all;
}

.modal-header {
    @apply px-6 py-4 border-b dark:border-slate-700 flex justify-between items-center text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.modal-close-btn {
    @apply p-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors;
}

.modal-footer {
    @apply p-4 bg-slate-50 dark:bg-slate-700/50 flex justify-end space-x-3 rounded-b-xl;
}

.modal-title {
    @apply mt-2 text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.modal-text {
    @apply mt-2 text-sm text-slate-600 dark:text-slate-400;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-20px);
}

.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.skeleton-card {
    @apply bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg animate-pulse space-y-4;
}

.view-fade-enter-active,
.view-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.view-fade-enter-from {
    opacity: 0;
    transform: translateX(-10px);
}

.view-fade-leave-to {
    opacity: 0;
    transform: translateX(10px);
}

.list-enter-active,
.list-leave-active {
    transition: all 0.4s ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

.notification-enter-active,
.notification-leave-active {
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.notification-enter-from,
.notification-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

.animate-pulse-fast {
    animation: pulse-fast 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse-fast {
    50% {
        opacity: .85;
    }
}

.card-portfolio {
    @apply bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg flex flex-col justify-between transition-all duration-300 hover:shadow-xl dark:hover:shadow-brand-700/30 cursor-pointer hover:-translate-y-1;
}

.empty-state {
    @apply text-center py-16 text-slate-500 dark:text-slate-400 bg-white dark:bg-brand-800 rounded-xl shadow-md flex flex-col items-center justify-center;
}

.danger-zone {
    @apply bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 p-5 rounded-2xl flex flex-col items-start gap-4;
}

.tag-owner {
    @apply ml-2 text-xs font-medium bg-slate-100 text-brand-700 dark:bg-brand-700 dark:text-brand-500 px-2 py-0.5 rounded-full;
}

.tag-pending {
    @apply ml-2 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-2 py-0.5 rounded-full;
}

.timeline-container {
    @apply border-l-2 border-slate-200 dark:border-slate-700;
}

.timeline-item {
    @apply relative pl-8 py-4;
}

.timeline-point {
    @apply bg-green-500 absolute -left-[9px] top-[28px] w-4 h-4 rounded-full border-4 border-white dark:border-brand-800;
}

.timeline-content {
    @apply ml-4;
}
</style>